import React from 'react'
import { connect } from 'react-redux'
import { object, bool } from 'prop-types'
import { css } from '@emotion/react'

import useHydrationContext from '@/hooks/useHydrationContext'
import {
  checkIfIsService,
  getItemPayment,
  isAiPoweredApp,
  isVerifiedApp,
} from '@/store/selectors'
import { useOnboarding } from '@/context/OnboardingContext'
import CheckIcon from '@/assets/icons/check.inline.svg'
import InfoIcon from '@/assets/icons/info.inline.svg'
import AiIcon from '@/assets/icons/wand.inline.svg'
import theme from '@/theme'

import OnboardingBadge from './OnboardingBadge'
import ProductBadge from './ProductBadge'
import BadgeTooltip from './BadgeTooltip'
import VerifiedAppTooltip from './VerifiedAppTooltip'

const onboardingBadgesWrapper = css`
  display: flex;
  flex-wrap: wrap;
`

export const ONBOARDING_BADGES = {
  installed: {
    label: 'App is installed',
    icon: CheckIcon,
    color: theme.colors.success,
  },
  permissions: {
    label: 'Please accept app permissions',
    icon: InfoIcon,
    color: theme.colors.orange500,
  },
  connect: {
    label: 'Please connect app',
    icon: InfoIcon,
    color: theme.colors.orange500,
  },
}

export const PRODUCT_BADGES = {
  trial: (days) => ({
    label: `${days}-day free trial`,
    color: theme.colors.lowEmphasisWarning,
  }),
  inAppUpgrades: {
    label: 'In-App Upgrades',
    color: theme.colors.gray40,
  },
  verified: {
    label: 'Verified',
    color: theme.colors.lowEmphasisPositive,
    tooltip: VerifiedAppTooltip,
  },
  aiPowered: {
    label: 'AI-Powered',
    icon: AiIcon,
    color: theme.colors.lowEmphasisPurpleAccent,
  },
}

const Badges = ({ payment, isService, isVerified, isAiPowered }) => {
  const { isInstalled, hasAcceptedConsent, isConfigured, isLoadingOnboarding } =
    useOnboarding()
  const hydrated = useHydrationContext()

  if (!hydrated || isLoadingOnboarding) {
    return null
  }

  const getBadges = () => {
    const onboarding = []
    const product = []

    // product badges
    if (payment?.trialDays > 0) {
      product.push(PRODUCT_BADGES.trial(payment.trialDays))
    }

    if (isAiPowered) {
      product.push(PRODUCT_BADGES.aiPowered)
    }

    if (isVerified) {
      product.push(PRODUCT_BADGES.verified)
    }

    // onboarding badges
    if (isInstalled && !isService) {
      onboarding.push(ONBOARDING_BADGES.installed)

      if (!hasAcceptedConsent) {
        onboarding.push(ONBOARDING_BADGES.permissions)
      } else if (hasAcceptedConsent && !isConfigured) {
        onboarding.push(ONBOARDING_BADGES.connect)
      }
    }

    return { onboarding, product }
  }

  const { onboarding, product } = getBadges()

  return (
    <>
      {product.map((badge) => (
        <BadgeTooltip tooltip={badge.tooltip} key={badge.label}>
          <ProductBadge {...badge} />
        </BadgeTooltip>
      ))}

      <div css={onboardingBadgesWrapper}>
        {onboarding.map((badge) => (
          <OnboardingBadge key={badge.label} {...badge} />
        ))}
      </div>
    </>
  )
}

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps

  const isService = checkIfIsService(state, id)
  const payment = getItemPayment(state, id)
  const isVerified = isVerifiedApp(state, id)
  const isAiPowered = isAiPoweredApp(state, id)

  return {
    isService,
    payment,
    isVerified,
    isAiPowered,
  }
}

Badges.propTypes = {
  isService: bool,
  payment: object,
  isVerified: bool,
  isAiPowered: bool,
}

export default connect(mapStateToProps, null)(Badges)
